import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RecipientCorrectionRequest } from '@lmc-app/summary/docusign/interface/docusign-envelope-recipients-interface';
import { CreateLoanAmendmentRequest } from '@lmc-app/summary/docusign/interface/docusign-loan-amendment-interface';
import { environment } from '@lmc-env/environment';
import { Observable } from 'rxjs';

interface Envelopes {
  row_id: number;
  application_id
  applicant_type: string;
  applicant_name: string;
  envelope_id: string;
  template_name: string;
  status_name: string;  
  full_name: string;
  createdBy: string;
  sentBy: string;
  created_at: string;
  sent_at: string;
}

export interface ExistingLoanAmendmentDetails {
  NLSLoanNumber: string;
  NLSPrincipalAmount: number;
  NLSOpenDate: string;
  NLSInterestRate: number;
}

export interface ExistingLoanAmendmentResponse {
  success: boolean;
  data: {
    foundLoan: boolean;
    loanDetails: ExistingLoanAmendmentDetails[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class DocusignService {
  private readonly uri: string = `${environment.DO_URL}/v2`;
  constructor(private readonly http: HttpClient) {}

  testLink(): Observable<any> {
    return this.http.get(`${this.uri}/testDocusignToken`);
  }

  getClientEnvelopes(id: string,type:string): Observable<Envelopes[]> {
    return this.http.get<Envelopes[]>(`${this.uri}/getClientEnvelopes/${id}/${type}`);
  }

  getEnvelopePreview(envelopeId: string): Observable<any> {
    return this.http.get(`${this.uri}/getEnvelopePreview/${envelopeId}`);
  }

  createEnvelope(data: any): Observable<any> {
    return this.http.post(`${this.uri}/createEnvelope`,{data});
  }

  sendEnvelope(data: any): Observable<any> {
    return this.http.post(`${this.uri}/sendEnvelope`,{data});
  }

  deleteEnvelope(data: any): Observable<any> {
    return this.http.post(`${this.uri}/deleteEnvelope`,{data});
  }

  uploadCompletedDocusign(data:any): Observable<any> {
    return this.http.post(`${this.uri}/uploadCompletedDocusign`,{data});
  }

  updateEnvelope(data:any): Observable<any> {
    return this.http.put(`${this.uri}/updateEnvelope`,{data});
  }

  streamEnvelope(envelopeId:string): Observable<Blob> {
    return this.http.get(`${this.uri}/streamClientEnvelope/${envelopeId}`, {
      responseType: 'blob' // no parsing
    });
  }

  getUpdatedZohoApplication(id:string): Observable<any> {
    return this.http.get(`${this.uri}/getUpdatedZohoApplication/${id}`);
  }

  getExistingLoanAmendment(loanNumber: string): Observable<ExistingLoanAmendmentResponse> {
    return this.http.get<ExistingLoanAmendmentResponse>(`${this.uri}/docusign/existingLoanAmendment/${encodeURIComponent(loanNumber)}`);
  }

  getDocusignLPPCancellation(data:any): Observable<any> {
    return this.http.post(`${this.uri}/getDocusignLPPCancellation`,{data});
  }

  reSendEnvelope(data: any): Observable<any> {
    return this.http.post(`${this.uri}/resendEnvelope`,{data});
  }

  normalizeAgreementData(application:any,userInfo:any): Observable<any> {
    return this.http.post(`${this.uri}/normalizeAgreementData`,{application,userInfo});
  }

  saveDocusignZohoChanges(id: string,application:any,contactId:string): Observable<any> {
    return this.http.post(`${this.uri}/saveDocusignZohoChanges`,{id,application,contactId});
  }

  createCompositeEnvelope(applicationData:any,userInfo:any): Observable<any> {
    return this.http.post(`${this.uri}/createCompositeEnvelope`,{applicationData,userInfo});
  }
  
  voidEnvelope(data:any): Observable<any> {
    return this.http.put(`${this.uri}/voidEnvelope`,{data});
  }

  updateCompositeEnvelope(applicationData:any,userInfo:any): Observable<any> {
    return this.http.post(`${this.uri}/updateAgreement`,{applicationData,userInfo});
  }

  currentEnvelopeRecipients(envelopeId: string): Observable<any> {
    return this.http.get(`${this.uri}/currentEnvelopeRecipients/${envelopeId}`);
  }

  getEnvelopeRecipientStatus(envelopeId: string): Observable<any> {
    return this.http.get(`${this.uri}/getEnvelopeRecipientStatus/${envelopeId}`);
  }

  getAgreementEnvelopes(id: string,type:string): Observable<Envelopes[]> {
    return this.http.get<Envelopes[]>(`${this.uri}/getAgreementEnvelopes/${id}/${type}`);
  }

  correctRecipient(envelopeId: string,payload: RecipientCorrectionRequest): Observable<{ success: boolean }> {
    return this.http.put<{ success: boolean }>(
      `${this.uri}/envelopes/${envelopeId}/recipients/${payload.recipientId}`,
      payload
    );
  }

  updateAgreementDetails(applicationData:any,userInfo:any): Observable<any> {
    return this.http.put(`${this.uri}/updateAgreementDetails`,{applicationData,userInfo});
  }

  // Creates a new Loan Amendment envelope (same response as createEnvelope)
  createLoanAmendment(data: CreateLoanAmendmentRequest): Observable<any> {
    return this.http.post(`${this.uri}/docusign/loan-amendments`, {data});
  }
  
}
